import { toast } from 'react-toastify';

import { enUS, he, ru } from 'date-fns/locale';
import { format } from 'date-fns';

// @ts-ignore
import domtoimage from 'dom-to-image-more';

export const getDuration = (dateFrom: string, dateTo: string) => {
  const dateFromObj = new Date(dateFrom);
  const dateToObj = new Date(dateTo);

  const duration = Math.abs(dateFromObj.getTime() - dateToObj.getTime());

  return getDurationFromTime(duration);
};

export const getDurationTicks = (dateFrom: string, dateTo: string) => {
  const dateFromObj = new Date(dateFrom);
  const dateToObj = new Date(dateTo);

  return Math.abs(dateFromObj.getTime() - dateToObj.getTime()).toString();
};

export const getDurationFromTime = (ticks: number, skipSpaces = false, showDays = true) => {
  const duration = ticks / 36e5;
  const days = Math.floor(duration / 24);
  const hours = Math.floor(duration - days * 24);
  const minutes = Math.floor((duration - days * 24 - hours) * 60);

  const daysStr = days > 0 ? `${days}d` : '';
  const hoursStr = !showDays && days > 0 ? `${days * 24 + hours}h` : hours > 0 ? `${hours}h` : '';
  const minutesStr = minutes > 0 ? `${minutes}m` : '';

  const parts = [];
  if (showDays && daysStr) parts.push(daysStr);
  if (hoursStr) parts.push(hoursStr);
  if (minutesStr) parts.push(minutesStr);

  const separator = skipSpaces ? '' : ' ';
  const result = parts.join(separator);

  return result || '0m';
};

export const addDays = (days: number, date?: Date) => {
  const dateObj = date || new Date();
  const time = dateObj.getTime();
  return new Date(time + days * 24 * 60 * 60 * 1000);
};

export const addYears = (years: number, date?: Date) => {
  const dateObj = date || new Date();
  return new Date(dateObj.setFullYear(dateObj.getFullYear() + years));
};

export const highlightStr = (str: string, highlight: string) => {
  const highlightArr = highlight.split(/[\s|]/);
  let result = str;

  highlightArr
    .filter((word) => word)
    .forEach((word) => {
      result = result.replace(new RegExp(word, 'gi'), (match) => {
        if ('<span class="highlight">'.includes(match) || '</span>'.includes(match)) {
          return match;
        }
        return `<span class="highlight">${match}</span>`;
      });
    });
  return result;
};

export const getDistanceByCoords = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const R = 6371; // km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    0.5 -
    Math.cos(dLat) / 2 +
    (Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * (1 - Math.cos(dLon))) /
      2;

  return (R * 2 * Math.asin(Math.sqrt(a))).toFixed(1);
};

export const searchCurrencyCode = sessionStorage.getItem('displayCurrencyHotels');

export const formatPriceCurrency = (price: number, currencySign: string, afterComma2?: boolean) => {
  if (!price) return '';

  const currencySymbolBefore = sessionStorage.getItem('currencySymbolBefore') === 'true';
  const value = afterComma2 ? price.toFixed(2) : Math.round(price);

  return currencySymbolBefore
    ? `${currencySign}${value.toLocaleString()}`
    : `${value.toLocaleString()}${currencySign}`;
};

export const formatPrice = (price: number, currencySign: string) => {
  if (!price) return '';
  const currency = getCurrency(currencySign);

  const currencySymbolBefore = sessionStorage.getItem('currencySymbolBefore') === 'true';

  return currencySymbolBefore
    ? `${currency}${price.toLocaleString()}`
    : `${price.toLocaleString()}${currency}`;
};

export const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const replaceHtml = (html: string) => {
  return html.replace(/&amp;lt;/g, '<').replaceAll(/&amp;gt;/g, '>');
};

export const openInNewWindow = (url: string) => {
  const currentParams = new URLSearchParams(window.location.search);
  const utmParams = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'gclid',
    'fbclid',
  ];

  const urlParams = new URLSearchParams(url.split('?')[1] || ''); // отримуємо параметри, якщо вони є в URL

  utmParams.forEach((param) => {
    if (currentParams.has(param)) {
      urlParams.set(param, currentParams.get(param)!);
    }
  });

  const updatedUrl = url.split('?')[0] + (urlParams.toString() ? '?' + urlParams.toString() : '');

  const newWindow = window.open(updatedUrl, '_blank');
  if (newWindow) {
    newWindow.focus();
  }
};

export const showError = (error?: string) => {
  toast(error || 'Error', {
    position: 'bottom-right',
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    progress: undefined,
    theme: 'colored',
    type: 'error',
  });
};

export const showSuccess = (message?: string) => {
  toast(message || 'Error', {
    position: 'bottom-right',
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    progress: undefined,
    theme: 'colored',
    type: 'success',
  });
};

export const showWarning = (warning?: string) => {
  toast(warning || 'Warning', {
    position: 'bottom-right',
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    progress: undefined,
    theme: 'colored',
    type: 'warning',
  });
};

export const includesManyHe = (str: string) => {
  if (!str) return false;
  const hebrew = /[\u0590-\u05FF]/;
  const hebrewSymbolsCount = str.split('').filter((char) => hebrew.test(char)).length;
  return hebrewSymbolsCount > str.length / 5;
};

export const getTextAlignStyle = (str: string): any => {
  if (!str) return {};
  return {
    textAlign: includesManyHe(str) ? 'right' : 'left',
  };
};

export const getCurrency = (currency: string) => {
  if (!currency) return '';
  const currencyUpper = currency.toUpperCase();

  switch (currencyUpper) {
    case 'USD':
      return '$';
    case 'EUR':
      return '€';
    case 'GBP':
      return '£';
    case 'LOC':
      return '₪';
    case 'NIS':
      return '₪';
    case 'ILS':
      return '₪';
    case 'THB':
      return '฿';
    default:
      return '';
  }
};

export const getCurrencyStr = (currency: string) => {
  switch (currency) {
    case '$':
      return 'USD';
    case '€':
      return 'EUR';
    case '£':
      return 'GBP';
    case '₪':
      return 'LOC';
    default:
      return '';
  }
};

export const getLogoUrl = (blobUrl: string, code?: string) => {
  if (!code) return '';
  return `${blobUrl}Airlines/${code}.png`;
};

export const getDaysDifference = (date1: string, date2: string) => {
  const date1Time = new Date(date1).setHours(0, 0, 0, 0);
  const date2Time = new Date(date2).setHours(0, 0, 0, 0);

  const oneDay = 24 * 60 * 60 * 1000;

  const diff = (date2Time - date1Time) / oneDay;

  return Math.round(diff);
};

export const getScript = (html: string, comment?: string) => {
  const str = replaceHtmlToStr(html);
  const fragment = document.createDocumentFragment();
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = str.trim();

  if (comment) {
    const startComment = document.createComment(` ${comment} `);
    fragment.appendChild(startComment);
  }

  const metaTags = tempDiv.querySelectorAll('meta');
  metaTags.forEach((meta) => {
    const newMeta = document.createElement('meta');
    Array.from(meta.attributes).forEach((attr) => {
      newMeta.setAttribute(attr.name, attr.value);
    });
    fragment.appendChild(newMeta);
  });

  const scriptElements = tempDiv.querySelectorAll('script');
  scriptElements.forEach((scriptElement) => {
    const newScript = document.createElement('script');
    Array.from(scriptElement.attributes).forEach((attr) => {
      newScript.setAttribute(attr.name, attr.value);
    });
    if (scriptElement.textContent) {
      newScript.text = scriptElement.textContent;
    }
    fragment.appendChild(newScript);
  });

  const noscriptElements = tempDiv.querySelectorAll('noscript');
  noscriptElements.forEach((noscriptElement) => {
    const newNoscript = document.createElement('noscript');
    const contentDiv = document.createElement('div');
    contentDiv.innerHTML = noscriptElement.innerHTML;
    while (contentDiv.firstChild) {
      newNoscript.appendChild(contentDiv.firstChild);
    }
    fragment.appendChild(newNoscript);
  });

  if (fragment.children.length === 0) {
    const script = document.createElement('script');
    try {
      script.type = 'text/javascript';
      script.text = str;
      script.async = true;
    } catch (err) {}
    fragment.appendChild(script);
  }

  if (comment) {
    const endComment = document.createComment(` End ${comment} `);
    fragment.appendChild(endComment);
  }

  return fragment;
};

export const replaceHtmlToStr = (html: string) => {
  if (!html) return '';

  let decoded = html;
  let prevDecoded = '';
  let iterations = 0;
  const maxIterations = 10;

  while (decoded !== prevDecoded && iterations < maxIterations) {
    prevDecoded = decoded;
    decoded = decoded
      .replaceAll('&amp;', '&')
      .replaceAll('&apos;', "'")
      .replaceAll('&lt;', '<')
      .replaceAll('&gt;', '>')
      .replaceAll('&nbsp;', ' ')
      .replaceAll('&quot;', '"');
    iterations++;
  }

  return decoded;
};

export const getIsRtl = () => {
  return (localStorage.getItem('language') || 'he') === 'he';
};

export const checkAHAVI = (input: string) => {
  if (!input) return false;
  const ahavi = ['א', 'ה', 'ו', 'י'];

  return ahavi.some((item) => input.includes(item));
};

export const dateFormat = (
  date: Date | string | undefined | number,
  formatStr: string,
  locale?: string,
) => {
  try {
    const lang = locale || localStorage.getItem('language');

    const dateObj = date == null ? new Date() : date instanceof Date ? date : new Date(date);

    switch (lang) {
      case 'he':
        return format(dateObj, formatStr, { locale: he });
      case 'ru':
        return format(dateObj, formatStr, { locale: ru });
      default:
        return format(dateObj, formatStr, { locale: enUS });
    }
  } catch (error) {
    return '';
  }
};
export interface Screenshot {
  name: string;
  data: string;
}

export const saveScreenshot = async (ref: any, screenShotName: string, screenshotKey: string) => {
  try {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.left = '-9999px';
    iframe.style.width = `${ref.offsetWidth}px`;
    iframe.style.height = `${ref.offsetHeight}px`;
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentDocument;
    if (!iframeDoc) throw new Error('Failed to access iframe document');

    iframeDoc.open();
    iframeDoc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <style>${getStyles()}</style> <!-- Include external styles -->
        </head>
        <body>${ref.outerHTML}</body>
      </html>
    `);
    iframeDoc.close();

    await new Promise((res) => setTimeout(res, 500));

    const data = await domtoimage.toJpeg(iframeDoc.body as HTMLElement, {
      quality: 0.4,
      filter: (element: HTMLElement) => element.tagName?.toLowerCase() !== 'img',
      bgcolor: '#F9FAFB',
    });

    document.body.removeChild(iframe);

    const key = 'screenshots' + screenshotKey;
    const newScreenshot = { name: screenShotName, data };
    const screenShots = JSON.parse(sessionStorage.getItem(key) || '[]');

    const index = screenShots.findIndex((screen: any) => screen.name === screenShotName);
    if (index !== -1) {
      screenShots[index] = newScreenshot;
    } else {
      screenShots.push(newScreenshot);
    }

    sessionStorage.setItem(key, JSON.stringify(screenShots));
  } catch (error) {
    console.error('Error:', error);
  }
};

export const getBase64Screenshot = async (ref: any): Promise<string> => {
  try {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.left = '-9999px';

    iframe.style.width = `${ref.offsetWidth}px`;
    iframe.style.height = `${ref.offsetHeight}px`;
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentDocument;
    if (!iframeDoc) throw new Error('Failed to access iframe document');

    iframeDoc.open();
    iframeDoc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <style>${getStyles()}</style>
        </head>
        <body>${ref.outerHTML}</body>
      </html>
    `);
    iframeDoc.close();

    await new Promise((res) => setTimeout(res, 500));

    const dataUrl = await domtoimage.toJpeg(iframeDoc.body, {
      quality: 0.4,
      filter: (element: any) => element.tagName?.toLowerCase() !== 'img',
      bgcolor: '#F9FAFB',
    });

    document.body.removeChild(iframe);
    return dataUrl;
  } catch (error) {
    console.error('Error:', error);
    return '';
  }
};

function getStyles() {
  const styles = document.styleSheets;
  let cssText = '';
  for (let i = 0; i < styles.length; i++) {
    const sheet = styles[i];
    if (sheet instanceof CSSStyleSheet) {
      try {
        const rules = sheet.cssRules;
        for (let j = 0; j < rules.length; j++) {
          cssText += rules[j].cssText;
        }
      } catch (e) {
        console.error('Error accessing styles from stylesheet: ', e);
      }
    }
  }
  return cssText;
}
